import { useRouter } from "next/router";
import { Stack, StackProps, Typography, TypographyProps, Avatar } from "@mui/material";
import AppButton, { AppButtonProps } from "components/v2/atoms/app-button/AppButton";
import { PageProps } from "pages/_app";
import { useEffect } from "react";
import useUserInfo, { UserInfo } from "hooks/user-info";
import React from "react";
import EmailIcon from "@mui/icons-material/Email";

export interface ContactUsProps {
    page?: string;
    pageProps: PageProps;
}

declare const window: {
    smartlook: (action: string, ...args: any[]) => void;
    open: (url: string, target: string) => void;
};

function ContactUs(props: ContactUsProps) {
    const router = useRouter();

    const [email, setEmail] = React.useState<string | null>(null);

    useEffect(() => {
        smartlookFn();
    }, []);

    const { fetchUserInfo } = useUserInfo({
        pageProps: props.pageProps,
        fetchOnInit: true,
    });

    const smartlookFn = async () => {
        try {
            if (typeof window.smartlook === "function") {
                const loggedUser: UserInfo = await fetchUserInfo();

                if (!loggedUser) return;

                setEmail(loggedUser?.email);

                window.smartlook("identify", loggedUser.email, {
                    name: `${loggedUser?.first_name}${loggedUser?.last_name ? ` ${loggedUser?.last_name}` : ""}`,
                    email: loggedUser.email,
                });
            }
        } catch (error) {
            console.log(`Error`, error);
        }
    };

    const teamMembers = [
        { name: "Shreeya Arora", role: "Product Owner" },
        { name: "Andrea Vucetich", role: "Product Owner" },
    ];

    const stackProps: StackProps = {
        sx: {
            display: "flex",
            justifyContent: "space-between",
            height: {
                xs: "100%",
                sm: "auto",
            },
        },
    };

    const innerStackProps: StackProps = {
        sx: {
            gap: 3,
            textAlign: "center",
            alignItems: "center",
        },
    };

    const teamMembersContainerProps: StackProps = {
        sx: {
            flexDirection: "row",
            justifyContent: "center",
            gap: 6,
        },
    };

    const headingProps: TypographyProps = {
        variant: "h2",
        children: "Talk to a product expert",
        sx: {
            fontSize: "28px",
            fontWeight: 600,
            color: "#1A1F24",
            lineHeight: 1.3,
            mt: 1,
        },
    };

    const descriptionProps: TypographyProps = {
        children: "Our sales team is available to help you find the right solution for your firm.",
        sx: {
            fontSize: "16px",
            color: "#7A8593",
            lineHeight: 1.6,
            maxWidth: "420px",
            mx: "auto",
        },
    };

    const buttonStackProps: StackProps = {
        sx: {
            gap: 1.5,
            width: "100%",
            mt: "auto",
            pt: 2,
        },
    };

    const primaryButtonProps: AppButtonProps = {
        id: "open-tax_contact-us_request-callback",
        children: "Request a callback",
        onClick() {
            let url = "/v2/open-tax/steps/callback";
            if (props.page) {
                url += `?page=${props.page}`;
            }
            router.push(url);
        },
        fullWidth: true,
        sx: {
            backgroundColor: "#1A1F24",
            color: "white",
            py: 1.75,
            fontSize: "16px",
            fontWeight: 500,
            borderRadius: "8px",
            textTransform: "none",
            "&:hover": {
                backgroundColor: "#2D3238",
            },
        },
    };

    const secondaryButtonProps: AppButtonProps = {
        id: "open-tax_contact-us_email",
        children: "Email us",
        variant: "outlined",
        onClick: () => window.open(`mailto:opentax@adsum-works.com`, "_blank"),
        fullWidth: true,
        sx: {
            borderColor: "#1A1F24",
            color: "#1A1F24",
            py: 1.75,
            fontSize: "16px",
            fontWeight: 500,
            borderRadius: "8px",
            textTransform: "none",
            "&:hover": {
                borderColor: "#2D3238",
                backgroundColor: "rgba(26, 31, 36, 0.04)",
            },
        },
    };

    return (
        <Stack {...stackProps}>
            <Stack {...innerStackProps}>
                {/* Team Members */}
                <Stack {...teamMembersContainerProps}>
                    {teamMembers.map((member, index) => (
                        <Stack key={index} sx={{ alignItems: "center", gap: 1.5, flex: 1 }}>
                            <Avatar
                                sx={{
                                    width: 96,
                                    height: 96,
                                    bgcolor: index === 0 ? "#8B7355" : "#556778",
                                    fontSize: "28px",
                                    fontWeight: 600,
                                }}>
                                {member.name
                                    .split(" ")
                                    .map(n => n[0])
                                    .join("")}
                            </Avatar>
                            <Stack sx={{ textAlign: "center", gap: 0.5 }}>
                                <Typography
                                    sx={{
                                        fontSize: "16px",
                                        fontWeight: 600,
                                        color: "#1A1F24",
                                    }}>
                                    {member.name}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: "14px",
                                        color: "#5A6672",
                                    }}>
                                    {member.role}
                                </Typography>
                            </Stack>
                        </Stack>
                    ))}
                </Stack>

                {/* Heading */}
                <Typography {...headingProps} />

                {/* Description */}
                <Typography {...descriptionProps} />

                {/* Email */}
                <Stack
                    sx={{
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        color: "#6B7684",
                        mt: -1,
                    }}>
                    <EmailIcon sx={{ fontSize: 20 }} />
                    <Typography sx={{ fontSize: "15px", color: "#6B7684" }}>opentax@adsum-works.com</Typography>
                </Stack>
            </Stack>

            {/* Buttons */}
            <Stack {...buttonStackProps}>
                <AppButton {...primaryButtonProps} />
                <AppButton {...secondaryButtonProps} />
            </Stack>
        </Stack>
    );
}

export default ContactUs;
